import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { useSocket } from '../../services/socket';
import { useIsWatch, useIsAnyWatch } from '../../hooks/useIsWatch';

// Interfaces
interface Dish {
  id: string;
  name: string;
  description: string;
  imagePath?: string;
  quantityLimit?: number;
  remainingQuantity?: number;
  isAvailable: boolean;
  noteOptions?: string[];
}

interface Allergy {
  id: string;
  name: string;
}

interface TableOrder {
  id: string;
  dishId: string;
  dishName: string;
  quantity: number;
  allergyIds: string[];
  allergyNames: string[];
  note?: string;
  updatedAt: string;
}

interface BatteryInfo {
  level: number;
  isCharging: boolean;
}

interface CurrentEvent {
  id: string;
  name: string;
  assignedTables: number[];
  currentBreak?: string;
}

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning';
}

const WaiterInterface: React.FC = () => {
  const { user, token, logout } = useAuthStore();
  const { socket, isConnected, emit } = useSocket();
  const navigate = useNavigate();
  const isWatch = useIsWatch();
  const isAnyWatch = useIsAnyWatch();

  // State
  const [selectedTable, setSelectedTable] = useState<number | null>(null);
  const [guestCount, setGuestCount] = useState(0);
  const [availableDishes, setAvailableDishes] = useState<Dish[]>([]);
  const [availableAllergies, setAvailableAllergies] = useState<Allergy[]>([]);
  const [tableOrders, setTableOrders] = useState<TableOrder[]>([]);
  const [assignedTables, setAssignedTables] = useState<number[]>([]);
  const [currentEvent, setCurrentEvent] = useState<CurrentEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState<string | null>(null);
  const [flashDish, setFlashDish] = useState<string | null>(null);
  const [battery, setBattery] = useState<BatteryInfo>({ level: 100, isCharging: false });
  const [showEmergencyMenu, setShowEmergencyMenu] = useState(false);
  const [showAllergyModal, setShowAllergyModal] = useState(false);
  const [selectedAllergies, setSelectedAllergies] = useState<string[]>([]);
  const [tableAllergies, setTableAllergies] = useState<string[]>([]); // Allergies for current table
  const [showDishInfo, setShowDishInfo] = useState<Dish | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [showGuestCountModal, setShowGuestCountModal] = useState(false);
  const [tempGuestCount, setTempGuestCount] = useState(0);
  const [showNoteModal, setShowNoteModal] = useState<Dish | null>(null);
  const [managerMessage, setManagerMessage] = useState<string | null>(null);

  // Toast helper
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 2500);
  }, []);

  // Load waiter's assigned tables and current event dishes
  const loadWaiterData = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoading(true);

      const verifyResponse = await fetch('/api/v1/auth/verify', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const verifyData = await verifyResponse.json();

      if (verifyData.success && verifyData.user?.currentEvent) {
        const eventData = verifyData.user.currentEvent;

        const breaksResponse = await fetch(`/api/v1/events/${eventData.id}/breaks`);
        const breaksData = await breaksResponse.json();

        let currentBreakName = 'מנה ראשונה';
        if (breaksData.success && breaksData.breaks) {
          const activeBreak = breaksData.breaks.find((b: any) => b.isActive) || breaksData.breaks[0];
          currentBreakName = activeBreak?.name || 'מנה ראשונה';

          if (activeBreak?.dishes) {
            const dishes: Dish[] = activeBreak.dishes.map((bd: any) => ({
              id: bd.dish.id,
              name: bd.dish.name,
              description: bd.dish.description || '',
              imagePath: bd.dish.imagePath,
              quantityLimit: bd.dish.quantityLimit,
              remainingQuantity: bd.remainingQuantity,
              isAvailable: bd.isSoldOut !== true && bd.dish.isAvailable !== false,
              noteOptions: bd.dish.noteOptions || undefined
            }));
            setAvailableDishes(dishes);
          }
        }

        setCurrentEvent({
          id: eventData.id,
          name: eventData.name,
          assignedTables: eventData.assignedTables || [],
          currentBreak: currentBreakName
        });
        setAssignedTables(eventData.assignedTables || []);

        const allergiesResponse = await fetch('/api/v1/allergies');
        const allergiesData = await allergiesResponse.json();
        if (allergiesData.success && allergiesData.allergies) {
          setAvailableAllergies(allergiesData.allergies);
        }
      }
    } catch (error) {
      console.error('Failed to load waiter data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Load orders for selected table
  const loadTableOrders = useCallback(async (tableNumber: number) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/v1/orders/table/${tableNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.orders) {
        const orders: TableOrder[] = data.orders.map((o: any) => ({
          id: o.id,
          dishId: o.dishId,
          dishName: o.dish?.name || 'Unknown',
          quantity: o.quantity,
          allergyIds: o.allergies?.map((a: any) => a.allergyId) || [],
          allergyNames: o.allergies?.map((a: any) => a.allergy?.name) || [],
          note: o.note || undefined,
          updatedAt: o.updatedAt || o.createdAt
        }));
        setTableOrders(orders);
        // If orders exist with guestCount, use that and update localStorage
        if (data.orders.length > 0 && data.orders[0].guestCount) {
          setGuestCount(data.orders[0].guestCount);
          localStorage.setItem(`guestCount-table-${tableNumber}`, data.orders[0].guestCount.toString());
        }
        // Extract table-level allergies from first order (they should all have the same)
        if (orders.length > 0 && orders[0].allergyIds.length > 0) {
          setTableAllergies(orders[0].allergyIds);
        } else {
          setTableAllergies([]);
        }
      }
    } catch (error) {
      console.error('Failed to load table orders:', error);
    }
  }, [token]);

  useEffect(() => {
    loadWaiterData();
    monitorBattery();

    if (socket && user?.id) {
      emit('join-room', `waiter-${user.id}`);
      emit('waiter-online', { waiterId: user.id, name: user.name });
    }

    if (socket) {
      socket.on('dishes-updated', loadWaiterData);
      socket.on('break-changed', (data: { breakName?: string }) => {
        loadWaiterData();
        if (data?.breakName) {
          setCurrentEvent(prev => prev ? { ...prev, currentBreak: data.breakName } : prev);
        }
        if (navigator.vibrate) {
          navigator.vibrate([200, 100, 200]);
        }
        showToast('המנה השתנתה!', 'warning');
      });
      socket.on('dish-sold-out', (dishId: string) => {
        setAvailableDishes(prev =>
          prev.map(d => d.id === dishId ? { ...d, isAvailable: false } : d)
        );
        showToast('מנה אזלה!', 'warning');
      });

      socket.on('force-logout', () => {
        showToast('נותקת על ידי המנהל', 'warning');
        setTimeout(() => {
          logout();
          navigate('/');
        }, 1000);
      });

      socket.on('event-ended', () => {
        showToast('האירוע הסתיים', 'warning');
        setTimeout(() => {
          logout();
          navigate('/');
        }, 2000);
      });

      // Listen for admin messages
      socket.on('notification', (notification: { type: string; title?: string; message: string; targetType: string }) => {
        // Only show messages targeted at waiters or all
        if (notification.type === 'message' && ['all', 'waiters', 'waiter', 'specific'].includes(notification.targetType)) {
          // Vibrate to get attention
          if (navigator.vibrate) {
            navigator.vibrate([300, 100, 300, 100, 300]);
          }
          // Show persistent modal instead of auto-dismissing toast
          setManagerMessage(notification.message);
        }
      });

      // Listen for table assignment updates
      socket.on('tables-updated', (data: { assignedTables: number[] }) => {
        setAssignedTables(data.assignedTables);
        setCurrentEvent(prev => prev ? { ...prev, assignedTables: data.assignedTables } : prev);
        showToast('השולחנות שלך עודכנו', 'success');
      });
    }

    return () => {
      if (socket) {
        socket.off('dishes-updated', loadWaiterData);
        socket.off('break-changed');
        socket.off('dish-sold-out');
        socket.off('force-logout');
        socket.off('event-ended');
        socket.off('notification');
        socket.off('tables-updated');
      }
    };
  }, [socket, user?.id, emit, loadWaiterData, showToast, logout, navigate]);

  useEffect(() => {
    if (selectedTable) {
      loadTableOrders(selectedTable);
    }
  }, [selectedTable, loadTableOrders]);

  const monitorBattery = async () => {
    if ('getBattery' in navigator) {
      try {
        const batteryManager = await (navigator as any).getBattery();
        setBattery({
          level: Math.round(batteryManager.level * 100),
          isCharging: batteryManager.charging
        });

        batteryManager.addEventListener('levelchange', () => {
          const newLevel = Math.round(batteryManager.level * 100);
          setBattery(prev => ({ ...prev, level: newLevel }));

          if (newLevel < 15 && user?.id) {
            emit('battery-low', {
              waiterId: user.id,
              waiterName: user.name,
              level: newLevel
            });
          }
        });

        batteryManager.addEventListener('chargingchange', () => {
          setBattery(prev => ({ ...prev, isCharging: batteryManager.charging }));
        });
      } catch (error) {
        console.log('Battery API not supported');
      }
    }
  };

  const selectTable = (tableNumber: number) => {
    setSelectedTable(tableNumber);
    // Load guest count from localStorage (fallback if no orders exist)
    const savedGuestCount = localStorage.getItem(`guestCount-table-${tableNumber}`);
    setGuestCount(savedGuestCount ? parseInt(savedGuestCount) : 0);
    setTableOrders([]);
    setTableAllergies([]); // Reset table allergies
  };

  const getDishQuantity = (dishId: string): number => {
    // Sum all orders for this dish (including different notes)
    return tableOrders
      .filter(o => o.dishId === dishId)
      .reduce((sum, o) => sum + o.quantity, 0);
  };

  // Get orders with notes for a dish (for sub-items display)
  const getDishNotedOrders = (dishId: string): Array<{ note: string; quantity: number }> => {
    return tableOrders
      .filter(o => o.dishId === dishId && o.note)
      .map(o => ({ note: o.note!, quantity: o.quantity }));
  };

  // Get table allergy names for display
  const getTableAllergyNames = (): string[] => {
    return availableAllergies.filter(a => tableAllergies.includes(a.id)).map(a => a.name);
  };

  const addDish = async (dish: Dish, note?: string) => {
    if (!selectedTable || !token || isSending) return;

    if (!dish.isAvailable) {
      showToast('המנה אזלה', 'error');
      return;
    }

    setIsSending(dish.id);

    try {
      // Find existing order with same dish AND same note (or both null)
      const existingOrder = tableOrders.find(o =>
        o.dishId === dish.id && (o.note || null) === (note || null)
      );

      if (existingOrder) {
        const response = await fetch(`/api/v1/orders/${existingOrder.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            quantity: existingOrder.quantity + 1
          })
        });
        const data = await response.json();

        if (data.success) {
          setTableOrders(prev =>
            prev.map(o => o.id === existingOrder.id
              ? { ...o, quantity: o.quantity + 1, updatedAt: new Date().toISOString() }
              : o
            )
          );
          setFlashDish(dish.id);
          setTimeout(() => setFlashDish(null), 300);
        } else {
          showToast(data.error || 'שגיאה', 'error');
        }
      } else {
        const response = await fetch('/api/v1/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            dishId: dish.id,
            tableNumber: selectedTable,
            quantity: 1,
            guestCount,
            allergyIds: tableAllergies,
            note: note || undefined
          })
        });
        const data = await response.json();

        if (data.success && data.order) {
          setTableOrders(prev => [...prev, {
            id: data.order.id,
            dishId: dish.id,
            dishName: dish.name,
            quantity: 1,
            allergyIds: tableAllergies,
            allergyNames: availableAllergies.filter(a => tableAllergies.includes(a.id)).map(a => a.name),
            note: note || undefined,
            updatedAt: new Date().toISOString()
          }]);
          setFlashDish(dish.id);
          setTimeout(() => setFlashDish(null), 300);
        } else {
          showToast(data.error || 'שגיאה', 'error');
        }
      }
    } catch (error) {
      console.error('Failed to add dish:', error);
      showToast('שגיאת תקשורת', 'error');
    } finally {
      setIsSending(null);
    }
  };

  const removeDish = async (dish: Dish) => {
    if (!selectedTable || !token || isSending) return;

    // Find all orders for this dish
    const dishOrders = tableOrders.filter(o => o.dishId === dish.id);
    if (dishOrders.length === 0) return;

    // LIFO: Find the most recently updated order for this dish
    const lastAddedOrder = dishOrders.reduce((latest, current) =>
      new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest
    );

    setIsSending(dish.id);

    try {
      if (lastAddedOrder.quantity > 1) {
        const response = await fetch(`/api/v1/orders/${lastAddedOrder.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            quantity: lastAddedOrder.quantity - 1
          })
        });
        const data = await response.json();

        if (data.success) {
          setTableOrders(prev =>
            prev.map(o => o.id === lastAddedOrder.id
              ? { ...o, quantity: o.quantity - 1, updatedAt: new Date().toISOString() }
              : o
            )
          );
          setFlashDish(dish.id);
          setTimeout(() => setFlashDish(null), 300);
        } else {
          showToast(data.error || 'שגיאה', 'error');
        }
      } else {
        const response = await fetch(`/api/v1/orders/${lastAddedOrder.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();

        if (data.success) {
          setTableOrders(prev => prev.filter(o => o.id !== lastAddedOrder.id));
          setFlashDish(dish.id);
          setTimeout(() => setFlashDish(null), 300);
        } else {
          showToast(data.error || 'שגיאה', 'error');
        }
      }
    } catch (error) {
      console.error('Failed to remove dish:', error);
      showToast('שגיאת תקשורת', 'error');
    } finally {
      setIsSending(null);
    }
  };

  const handleAllergyConfirm = async () => {
    if (!showAllergyModal || !token) return;

    // Update table allergies state
    setTableAllergies(selectedAllergies);

    // Update ALL existing orders for this table with the new allergies
    const allergyNames = availableAllergies.filter(a => selectedAllergies.includes(a.id)).map(a => a.name);

    try {
      // Update each order in parallel
      const updatePromises = tableOrders.map(order =>
        fetch(`/api/v1/orders/${order.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            allergyIds: selectedAllergies
          })
        })
      );

      await Promise.all(updatePromises);

      // Update local state
      setTableOrders(prev =>
        prev.map(o => ({
          ...o,
          allergyIds: selectedAllergies,
          allergyNames
        }))
      );
      showToast('אלרגיות לשולחן עודכנו', 'success');
    } catch (error) {
      showToast('שגיאת תקשורת', 'error');
    }

    setShowAllergyModal(false);
    setSelectedAllergies([]);
  };

  const handleEmergencyAction = (type: 'call-manager' | 'broken-dish') => {
    const tableNum = selectedTable || 0;
    const message = type === 'call-manager' ? 'קריאה דחופה' : 'כלי שבור';

    try {
      emit('emergency-call', {
        type,
        tableNumber: tableNum,
        waiterId: user?.id,
        waiterName: user?.name,
        message,
        timestamp: new Date().toISOString()
      });

      showToast(`${message} נשלח`, 'success');
      setShowEmergencyMenu(false);
    } catch (error) {
      console.error('Failed to send emergency alert:', error);
      showToast('שגיאה', 'error');
    }
  };

  // Toast component - positioned for small screens
  const ToastContainer = () => (
    <div className="fixed top-2 left-2 right-2 z-50 space-y-1">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`p-2 rounded-lg text-center text-white text-sm font-bold ${
            toast.type === 'success' ? 'bg-green-600' :
            toast.type === 'error' ? 'bg-red-600' :
            'bg-amber-600'
          }`}
        >
          {toast.message}
        </div>
      ))}
    </div>
  );

  // Manager message modal - persistent until dismissed
  const ManagerMessageModal = () => managerMessage ? (
    <div className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
      <div className="w-full max-w-xs bg-zinc-900 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-4 watchlg:p-3 watch:p-3 border-2 border-amber-500">
        <div className="text-center mb-3 watchlg:mb-2 watch:mb-2">
          <svg className="w-10 h-10 watchlg:w-8 watchlg:h-8 watch:w-7 watch:h-7 text-amber-400 mx-auto mb-2 watch:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          <h3 className="text-lg watchlg:text-base watch:text-base font-bold text-amber-400">הודעה מהמנהל</h3>
        </div>
        <p className="text-white text-center text-lg watchlg:text-base watch:text-sm font-medium mb-4 watchlg:mb-3 watch:mb-3">{managerMessage}</p>
        <button
          onClick={() => setManagerMessage(null)}
          className="w-full bg-amber-600 active:bg-amber-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
        >
          אישור
        </button>
      </div>
    </div>
  ) : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-white border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-gray-400 text-lg">טוען...</p>
        </div>
      </div>
    );
  }

  // Table selection view - Optimized for smartwatch
  if (!selectedTable) {
    return (
      <div className="min-h-screen bg-black text-white">
        <ToastContainer />
        <ManagerMessageModal />

        {/* Compact Header */}
        <div className="p-3 watchlg:px-2 watchlg:py-1.5 watch:px-1.5 watch:py-1 bg-zinc-900">
          {/* Watch: name on its own row */}
          <div className="hidden watch:flex items-center gap-1 mb-1">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm font-bold truncate">{user?.name || 'מלצר'}</span>
          </div>
          <div className="flex justify-between items-center">
            {/* Phone: name inline */}
            <div className="flex items-center gap-2 watch:hidden">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-base watchlg:text-sm font-bold truncate max-w-[100px]">{user?.name || 'מלצר'}</span>
            </div>
            <div className="flex items-center gap-2 watchlg:gap-1.5 watch:gap-1">
              <button
                onClick={() => setShowEmergencyMenu(true)}
                className="bg-red-600 text-white text-sm watch:text-xs font-bold px-3 py-1.5 watchlg:px-2.5 watchlg:py-1 watch:px-2 watch:py-1 rounded-lg"
              >
                חירום
              </button>
              <span className="text-sm watch:text-xs text-gray-400">{battery.level}%</span>
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="bg-zinc-700 text-white text-sm watch:text-xs font-bold px-3 py-1.5 watchlg:px-2.5 watchlg:py-1 watch:px-2 watch:py-1 rounded-lg"
              >
                יציאה
              </button>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center py-4 watchlg:py-3 watch:py-2">
          <h1 className="text-2xl watchlg:text-xl watch:text-lg font-bold">בחר שולחן</h1>
          {currentEvent && (
            <p className="text-gray-400 text-base watch:text-sm mt-1">{currentEvent.name}</p>
          )}
        </div>

        {/* Table Grid - Large touch targets */}
        <div className="p-3 watchlg:p-2.5 watch:p-2 grid grid-cols-2 gap-3 watchlg:gap-2 watch:gap-1.5">
          {assignedTables.map((table) => (
            <button
              key={table}
              onClick={() => selectTable(table)}
              className="bg-zinc-800 active:bg-zinc-700 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-6 watchlg:p-4 watch:p-3 text-center"
            >
              <div className="text-4xl watchlg:text-3xl watch:text-2xl font-bold">{table}</div>
              <div className="text-gray-400 text-base watchlg:text-sm watch:text-xs mt-1">שולחן</div>
            </button>
          ))}
        </div>

        {assignedTables.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-lg">
            לא הוקצו שולחנות
          </div>
        )}

        {/* Emergency Modal */}
        {showEmergencyMenu && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
            <div className="w-full max-w-xs bg-zinc-900 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-4 watchlg:p-3 watch:p-2">
              <h3 className="text-xl watchlg:text-lg watch:text-base font-bold text-center mb-4 watchlg:mb-3 watch:mb-2">קריאת חירום</h3>
              <div className="space-y-3 watchlg:space-y-2 watch:space-y-2">
                <button
                  onClick={() => handleEmergencyAction('call-manager')}
                  className="w-full bg-red-600 active:bg-red-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-4 watchlg:py-3 watch:py-2 rounded-xl"
                >
                  קרא למנהל
                </button>
                <button
                  onClick={() => handleEmergencyAction('broken-dish')}
                  className="w-full bg-amber-600 active:bg-amber-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-4 watchlg:py-3 watch:py-2 rounded-xl"
                >
                  כלי שבור
                </button>
                <button
                  onClick={() => setShowEmergencyMenu(false)}
                  className="w-full bg-zinc-700 active:bg-zinc-600 text-white text-lg watchlg:text-base watch:text-sm font-medium py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
                >
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main ordering interface - Optimized for smartwatch/mobile
  return (
    <div className="min-h-screen bg-black text-white">
      <ToastContainer />
      <ManagerMessageModal />

      {/* Sticky Header - Compact */}
      <div className="sticky top-0 z-10 bg-zinc-900">
        {/* Table Tabs - Horizontal scroll */}
        <div className="flex overflow-x-auto gap-2 watchlg:gap-1.5 watch:gap-1 p-2 watchlg:p-1.5 watch:p-1">
          {/* Back button */}
          <button
            onClick={() => setSelectedTable(null)}
            className="flex-shrink-0 px-3 py-2 watchlg:px-2.5 watchlg:py-1.5 watch:px-2 watch:py-1 rounded-xl watch:rounded-lg text-base watchlg:text-sm watch:text-sm font-bold bg-zinc-700 text-white flex items-center gap-1"
          >
            <svg className="w-4 h-4 watchlg:w-3.5 watchlg:h-3.5 watch:w-3 watch:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {isAnyWatch ? '←' : 'חזור'}
          </button>
          {assignedTables.map((table) => (
            <button
              key={table}
              onClick={() => selectTable(table)}
              className={`flex-shrink-0 px-4 py-2 watchlg:px-3 watchlg:py-1.5 watch:px-2.5 watch:py-1 rounded-xl watch:rounded-lg text-base watchlg:text-sm watch:text-sm font-bold ${
                selectedTable === table
                  ? 'bg-white text-black'
                  : 'bg-zinc-800 text-white'
              }`}
            >
              {table}
            </button>
          ))}
        </div>

        {/* Info Bar - on watch, merged into a single compact row */}
        {isWatch ? (
          <div className="flex items-center justify-between px-1.5 py-1 border-t border-zinc-800">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold">#{selectedTable}</span>
              {currentEvent?.currentBreak && (
                <span className="bg-blue-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                  {currentEvent.currentBreak}
                </span>
              )}
              {tableAllergies.length > 0 && (
                <button
                  onClick={() => { setSelectedAllergies(tableAllergies); setShowAllergyModal(true); }}
                  className="bg-amber-600 text-white text-xs font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5"
                >
                  ⚠ {tableAllergies.length}
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              {tableAllergies.length === 0 && (
                <button
                  onClick={() => { setSelectedAllergies(tableAllergies); setShowAllergyModal(true); }}
                  className="bg-zinc-800 p-1 rounded"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => { setTempGuestCount(guestCount); setShowGuestCountModal(true); }}
                className="bg-zinc-800 px-1.5 py-0.5 rounded flex items-center gap-0.5"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                <span className="text-xs font-bold">{guestCount}</span>
              </button>
              <button
                onClick={() => setShowEmergencyMenu(true)}
                className="bg-red-600 p-1 rounded"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </button>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            </div>
          </div>
        ) : (
          <>
            {/* Info Bar - phone layout */}
            <div className="flex justify-between items-center px-3 py-2 watchlg:px-2 watchlg:py-1.5 border-t border-zinc-800">
              <div className="flex items-center gap-3 watchlg:gap-2">
                <span className="text-xl watchlg:text-lg font-bold">שולחן {selectedTable}</span>
                {currentEvent?.currentBreak && (
                  <span className="bg-blue-600 text-white text-sm watchlg:text-xs font-bold px-2 py-1 watchlg:px-1.5 watchlg:py-0.5 rounded">
                    {currentEvent.currentBreak}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 watchlg:gap-1.5">
                <button
                  onClick={() => { setSelectedAllergies(tableAllergies); setShowAllergyModal(true); }}
                  className={`flex items-center gap-1 px-3 py-2 watchlg:px-2 watchlg:py-1.5 rounded-lg ${
                    tableAllergies.length > 0 ? 'bg-amber-600' : 'bg-zinc-800'
                  }`}
                >
                  <svg className="w-5 h-5 watchlg:w-4 watchlg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm watchlg:text-xs font-bold">{tableAllergies.length > 0 ? tableAllergies.length : ''}</span>
                </button>
                <button
                  onClick={() => { setTempGuestCount(guestCount); setShowGuestCountModal(true); }}
                  className="flex items-center gap-1 bg-zinc-800 px-3 py-2 watchlg:px-2 watchlg:py-1.5 rounded-lg"
                >
                  <svg className="w-5 h-5 watchlg:w-4 watchlg:h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <span className="text-lg watchlg:text-base font-bold">{guestCount}</span>
                </button>
                <button
                  onClick={() => setShowEmergencyMenu(true)}
                  className="bg-red-600 px-3 py-2 watchlg:px-2 watchlg:py-1.5 rounded-lg"
                >
                  <svg className="w-5 h-5 watchlg:w-4 watchlg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>
                <div className={`w-3 h-3 watchlg:w-2.5 watchlg:h-2.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              </div>
            </div>

            {/* Table Allergies Display - phone only */}
            {tableAllergies.length > 0 && (
              <div className="px-3 py-2 watchlg:px-2 watchlg:py-1.5 bg-amber-600/20 border-t border-amber-600">
                <div className="flex flex-wrap gap-1 items-center">
                  <span className="text-amber-400 text-sm watchlg:text-xs font-bold ml-2">אלרגיות:</span>
                  {getTableAllergyNames().map((name, idx) => (
                    <span key={idx} className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded font-medium">
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Dishes List - Optimized for touch */}
      <div className="p-2 watchlg:p-1.5 watch:p-1 space-y-2 watchlg:space-y-1.5 watch:space-y-1">
        {availableDishes.length > 0 ? (
          availableDishes.map((dish) => {
            const quantity = getDishQuantity(dish.id);
            const isFlashing = flashDish === dish.id;
            const isProcessing = isSending === dish.id;
            const dishAvailable = dish.isAvailable !== false;

            return (
              <div
                key={dish.id}
                className={`rounded-2xl watchlg:rounded-xl watch:rounded-xl p-3 watchlg:p-2 watch:p-1.5 transition-all ${
                  isFlashing ? 'bg-green-600' :
                  !dishAvailable ? 'bg-zinc-900 opacity-50' :
                  quantity > 0 ? 'bg-green-900/50 border-2 border-green-500' :
                  'bg-zinc-800'
                }`}
              >
                {/* Dish Info Row */}
                <div className="flex justify-between items-start mb-2 watchlg:mb-1.5 watch:mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 watchlg:gap-1.5 watch:gap-1">
                      <h3 className="text-lg watchlg:text-base watch:text-sm font-bold truncate">{dish.name}</h3>
                      <button
                        onClick={() => setShowDishInfo(dish)}
                        className="text-gray-400 flex-shrink-0"
                      >
                        <svg className="w-5 h-5 watchlg:w-4 watchlg:h-4 watch:w-4 watch:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                    {dish.quantityLimit && dish.remainingQuantity !== undefined && (
                      <p className="text-sm watchlg:text-xs watch:text-xs text-amber-400 mt-0.5">
                        נותרו: {dish.remainingQuantity}
                      </p>
                    )}
                    {!dishAvailable && (
                      <p className="text-red-400 text-sm watchlg:text-xs watch:text-xs font-bold">אזל</p>
                    )}
                  </div>
                  {quantity > 0 && (
                    <div className="bg-green-600 rounded-full w-12 h-12 watchlg:w-10 watchlg:h-10 watch:w-8 watch:h-8 flex items-center justify-center font-bold text-2xl watchlg:text-xl watch:text-base flex-shrink-0 mr-2 watchlg:mr-1.5 watch:mr-1">
                      {quantity}
                    </div>
                  )}
                </div>

                {/* Sub-items for noted orders */}
                {getDishNotedOrders(dish.id).length > 0 && (
                  <div className="mb-2 watchlg:mb-1.5 watch:mb-1 pr-4 watchlg:pr-3 watch:pr-2">
                    {getDishNotedOrders(dish.id).map((notedOrder, idx) => (
                      <div key={idx} className="flex items-center gap-2 watchlg:gap-1.5 watch:gap-1 text-sm watchlg:text-xs watch:text-xs text-purple-400">
                        <span className="text-gray-500">└─</span>
                        <span>{notedOrder.note}</span>
                        <span className="text-purple-300">({notedOrder.quantity})</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons - Large touch targets, fixed height */}
                <div className="flex gap-2 watchlg:gap-1.5 watch:gap-1 h-12 watchlg:h-10 watch:h-8">
                  <button
                    onClick={() => addDish(dish)}
                    disabled={!dishAvailable || isProcessing}
                    className={`flex-1 text-lg watchlg:text-base watch:text-sm font-bold rounded-xl watch:rounded-lg flex items-center justify-center ${
                      !dishAvailable || isProcessing
                        ? 'bg-zinc-700 text-zinc-500'
                        : 'bg-green-600 active:bg-green-700 text-white'
                    }`}
                  >
                    {isProcessing ? (
                      <div className="animate-spin h-6 w-6 watchlg:h-5 watchlg:w-5 watch:h-4 watch:w-4 border-3 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <>+ הוסף</>
                    )}
                  </button>

                  {/* Note button - only if dish has noteOptions */}
                  {dish.noteOptions && dish.noteOptions.length > 0 && (
                    <button
                      onClick={() => setShowNoteModal(dish)}
                      disabled={!dishAvailable || isProcessing}
                      className={`text-lg watchlg:text-base watch:text-sm font-bold px-4 watchlg:px-3 watch:px-2 rounded-xl watch:rounded-lg flex items-center justify-center ${
                        !dishAvailable || isProcessing
                          ? 'bg-zinc-700 text-zinc-500'
                          : 'bg-purple-600 active:bg-purple-700 text-white'
                      }`}
                      title="הוסף עם הערה"
                    >
                      📝
                    </button>
                  )}

                  {quantity > 0 && (
                    <button
                      onClick={() => removeDish(dish)}
                      disabled={isProcessing}
                      className="bg-red-600 active:bg-red-700 text-white text-lg watchlg:text-base watch:text-sm font-bold px-5 watchlg:px-4 watch:px-3 rounded-xl watch:rounded-lg flex items-center justify-center"
                    >
                      -
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 watchlg:py-8 watch:py-6 text-gray-400 text-lg watchlg:text-base watch:text-sm">
            אין מנות זמינות
          </div>
        )}
      </div>

      {/* Dish Info Modal */}
      {showDishInfo && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
          <div className="w-full max-w-xs bg-zinc-900 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-4 watchlg:p-3 watch:p-2">
            <h3 className="text-xl watchlg:text-lg watch:text-base font-bold mb-2 watchlg:mb-1.5 watch:mb-1">{showDishInfo.name}</h3>
            <p className="text-gray-300 mb-4 watchlg:mb-3 watch:mb-2 watchlg:text-sm watch:text-sm">{showDishInfo.description || 'אין תיאור'}</p>
            {showDishInfo.quantityLimit && (
              <p className="text-amber-400 text-sm watchlg:text-xs watch:text-xs mb-4 watchlg:mb-3 watch:mb-2">כמות מוגבלת: {showDishInfo.quantityLimit}</p>
            )}
            <button
              onClick={() => setShowDishInfo(null)}
              className="w-full bg-blue-600 active:bg-blue-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
            >
              סגור
            </button>
          </div>
        </div>
      )}

      {/* Allergy Modal - Table Level */}
      {showAllergyModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
          <div className="w-full max-w-xs bg-zinc-900 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-4 watchlg:p-3 watch:p-2 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl watchlg:text-lg watch:text-base font-bold mb-4 watchlg:mb-3 watch:mb-2 text-center">אלרגיות לשולחן {selectedTable}</h3>
            <div className="space-y-2 watchlg:space-y-1.5 watch:space-y-1 mb-4 watchlg:mb-3 watch:mb-2">
              {availableAllergies.map((allergy) => (
                <label
                  key={allergy.id}
                  className={`flex items-center gap-3 watchlg:gap-2 watch:gap-2 p-3 watchlg:p-2.5 watch:p-2 rounded-xl watch:rounded-lg cursor-pointer ${
                    selectedAllergies.includes(allergy.id) ? 'bg-amber-600' : 'bg-zinc-800'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedAllergies.includes(allergy.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAllergies(prev => [...prev, allergy.id]);
                      } else {
                        setSelectedAllergies(prev => prev.filter(id => id !== allergy.id));
                      }
                    }}
                    className="w-6 h-6 watchlg:w-5 watchlg:h-5 watch:w-5 watch:h-5 rounded"
                  />
                  <span className="text-lg watchlg:text-base watch:text-sm font-medium">{allergy.name}</span>
                </label>
              ))}
              {availableAllergies.length === 0 && (
                <p className="text-center text-gray-400 py-4 watchlg:py-3 watch:py-2 watchlg:text-sm watch:text-sm">אין אלרגיות במערכת</p>
              )}
            </div>
            <div className="space-y-2 watchlg:space-y-1.5 watch:space-y-1">
              <button
                onClick={handleAllergyConfirm}
                className="w-full bg-amber-600 active:bg-amber-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
              >
                אישור ({selectedAllergies.length})
              </button>
              <button
                onClick={() => { setShowAllergyModal(false); setSelectedAllergies([]); }}
                className="w-full bg-zinc-700 active:bg-zinc-600 text-white text-lg watchlg:text-base watch:text-sm font-medium py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Modal */}
      {showEmergencyMenu && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
          <div className="w-full max-w-xs bg-zinc-900 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-4 watchlg:p-3 watch:p-2">
            <h3 className="text-xl watchlg:text-lg watch:text-base font-bold text-center mb-4 watchlg:mb-3 watch:mb-2">קריאת חירום</h3>
            <div className="space-y-3 watchlg:space-y-2 watch:space-y-2">
              <button
                onClick={() => handleEmergencyAction('call-manager')}
                className="w-full bg-red-600 active:bg-red-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-4 watchlg:py-3 watch:py-2 rounded-xl"
              >
                קרא למנהל
              </button>
              <button
                onClick={() => handleEmergencyAction('broken-dish')}
                className="w-full bg-amber-600 active:bg-amber-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-4 watchlg:py-3 watch:py-2 rounded-xl"
              >
                כלי שבור
              </button>
              <button
                onClick={() => setShowEmergencyMenu(false)}
                className="w-full bg-zinc-700 active:bg-zinc-600 text-white text-lg watchlg:text-base watch:text-sm font-medium py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest Count Modal */}
      {showGuestCountModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
          <div className="w-full max-w-xs bg-zinc-900 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-4 watchlg:p-3 watch:p-2">
            <h3 className="text-xl watchlg:text-lg watch:text-base font-bold text-center mb-4 watchlg:mb-3 watch:mb-2">מספר סועדים</h3>

            {/* Current count with +/- buttons */}
            <div className="flex items-center justify-center gap-4 watchlg:gap-3 watch:gap-2 mb-4 watchlg:mb-3 watch:mb-2">
              <button
                onClick={() => setTempGuestCount(prev => Math.max(1, prev - 1))}
                className="w-16 h-16 watchlg:w-13 watchlg:h-13 watch:w-11 watch:h-11 rounded-xl bg-red-600 active:bg-red-700 text-white text-3xl watchlg:text-2xl watch:text-xl font-bold"
              >
                -
              </button>
              <div className="w-20 h-20 watchlg:w-16 watchlg:h-16 watch:w-14 watch:h-14 rounded-xl bg-zinc-800 flex items-center justify-center">
                <span className="text-4xl watchlg:text-3xl watch:text-2xl font-bold text-white">{tempGuestCount}</span>
              </div>
              <button
                onClick={() => setTempGuestCount(prev => prev + 1)}
                className="w-16 h-16 watchlg:w-13 watchlg:h-13 watch:w-11 watch:h-11 rounded-xl bg-green-600 active:bg-green-700 text-white text-3xl watchlg:text-2xl watch:text-xl font-bold"
              >
                +
              </button>
            </div>

            {/* Preset buttons */}
            <div className="grid grid-cols-5 gap-2 watchlg:gap-1.5 watch:gap-1 mb-4 watchlg:mb-3 watch:mb-2">
              {[3, 5, 9, 12, 15].map((num) => (
                <button
                  key={num}
                  onClick={() => setTempGuestCount(num)}
                  className={`py-3 watchlg:py-2 watch:py-1.5 rounded-xl watch:rounded-lg text-lg watchlg:text-base watch:text-sm font-bold ${
                    tempGuestCount === num
                      ? 'bg-blue-600 text-white'
                      : 'bg-zinc-800 text-white'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>

            <div className="space-y-2 watchlg:space-y-1.5 watch:space-y-1">
              <button
                onClick={async () => {
                  if (!selectedTable) return;

                  // Always save to localStorage first (persists even without orders)
                  localStorage.setItem(`guestCount-table-${selectedTable}`, tempGuestCount.toString());
                  setGuestCount(tempGuestCount);

                  // If authenticated, also try to update orders (if any exist)
                  if (token) {
                    try {
                      await fetch(`/api/v1/orders/table/${selectedTable}/guest-count`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ guestCount: tempGuestCount })
                      });
                    } catch (error) {
                      // Silent fail - localStorage already saved
                      console.log('Failed to update orders, but localStorage saved');
                    }
                  }

                  showToast('מספר סועדים עודכן', 'success');
                  setShowGuestCountModal(false);
                }}
                className="w-full bg-blue-600 active:bg-blue-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
              >
                אישור
              </button>
              <button
                onClick={() => setShowGuestCountModal(false)}
                className="w-full bg-zinc-700 active:bg-zinc-600 text-white text-lg watchlg:text-base watch:text-sm font-medium py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Selection Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 watchlg:p-3 watch:p-2">
          <div className="w-full max-w-xs bg-zinc-900 rounded-2xl watchlg:rounded-xl watch:rounded-xl p-4 watchlg:p-3 watch:p-2">
            <h3 className="text-xl watchlg:text-lg watch:text-base font-bold text-center mb-4 watchlg:mb-3 watch:mb-2">
              בחר הערה: {showNoteModal.name}
            </h3>

            <div className="space-y-2 watchlg:space-y-1.5 watch:space-y-1 mb-4 watchlg:mb-3 watch:mb-2">
              {showNoteModal.noteOptions?.map((option) => (
                <button
                  key={option}
                  onClick={() => {
                    addDish(showNoteModal, option);
                    setShowNoteModal(null);
                  }}
                  className="w-full bg-purple-600 active:bg-purple-700 text-white text-lg watchlg:text-base watch:text-sm font-bold py-4 watchlg:py-3 watch:py-2 rounded-xl"
                >
                  {option}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowNoteModal(null)}
              className="w-full bg-zinc-700 active:bg-zinc-600 text-white text-lg watchlg:text-base watch:text-sm font-medium py-3 watchlg:py-2.5 watch:py-2 rounded-xl"
            >
              ביטול
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaiterInterface;
